import { useGraphQLQuery, ServicePath } from "@opengov/bnp-core";
import { gql } from "graphql-request";
import { useMemo } from "react";
import { Entitlement } from "../types";

const query = gql`
  query getEntityAppEntitlements($userUUID: String!, $entityID: String!) {
    entityAppEntitlements(userUUID: $userUUID, entityID: [$entityID]) {
      entityUuid
      entitlements {
        displayName
        entitlementProductKey
      }
    }
  }
`;

type GetEntityAppEntitlementsQueryResponse = {
  entityAppEntitlements: {
    entityUuid: string;
    entitlements: Entitlement[];
  }[];
};

type GetEntityAppEntitlementsVariables = {
  userUUID: string;
  entityID: string;
};

export const useGetEntityAppEntitlements = ({
  appRequireEntitlements,
  entityUuid,
  userUuid,
}: {
  appRequireEntitlements: boolean;
  entityUuid: string | undefined;
  userUuid: string;
}) => {
  const isFetchEnabled = Boolean(appRequireEntitlements && entityUuid && userUuid);
  const { data, isLoading } = useGraphQLQuery<GetEntityAppEntitlementsQueryResponse, GetEntityAppEntitlementsVariables>(
    {
      queryKey: ["getEntityAppEntitlements", `entityUuid: ${entityUuid}, userUuid: ${userUuid}`] as const,
      document: query,
      variables: {
        userUUID: userUuid,
        entityID: entityUuid || "",
      },
      url: ServicePath.AccessServiceGraphql,
      options: {
        enabled: isFetchEnabled,
      },
    },
  );

  const entitlements = useMemo(() => {
    if (!isFetchEnabled || isLoading) return [];
    const entityEntitlements = data?.entityAppEntitlements.find((entitlement) => entitlement.entityUuid === entityUuid);
    if (!entityEntitlements) {
      console.error("Entity entitlements not found");
      return [];
    }
    return entityEntitlements.entitlements;
  }, [isFetchEnabled, isLoading, data, entityUuid]);

  return entitlements;
};
